import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Trophy, Sparkles, ChevronRight, Award, Lock, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GAMES, PROTECTIVE_FACTORS } from "../data/games";
import { HEROES } from "../data/heroes";
import GameShell, { pick } from "./games/GameShell";
import Quiz from "./games/Quiz";
import Match from "./games/Match";
import Tap from "./games/Tap";
import Sequence from "./games/Sequence";
import Breath from "./games/Breath";

const HERO_ORDER = ["queen", "victor", "cam"];

const useLang = () => {
  const { i18n } = useTranslation();
  return (i18n.language || "en").slice(0, 2);
};

const RUNNERS = { quiz: Quiz, match: Match, tap: Tap, sequence: Sequence, breath: Breath };

// ---------- Hero celebration (LLM paraphrase) ----------
const useHeroCelebration = (hero, lang) => {
  const [line, setLine] = useState(null);
  const fetchLine = async (seed) => {
    try {
      const r = await axios.post("/hero/paraphrase", {
        hero, text: seed, language: lang, style: "celebrate", variations: 1,
      });
      const v = r.data?.variations?.[0];
      if (v) setLine(v);
    } catch {
      setLine(seed);
    }
  };
  return [line, fetchLine];
};

// ---------- Result screen ----------
const ResultScreen = ({ game, level, result, lang, onReplay, onNext, onClose, hasNextLevel }) => {
  const [line, fetchLine] = useHeroCelebration(game.hero, lang);
  useEffect(() => {
    const baseSeed = lang === "es"
      ? `Acabo de jugar ${pick(lang, game.title)} Nivel ${level} y conseguí ${result.score} de ${result.total}.`
      : `I just played ${pick(lang, game.title)} Level ${level} and scored ${result.score} out of ${result.total}.`;
    fetchLine(baseSeed);
    // eslint-disable-next-line
  }, []);

  return (
    <GameShell hero={game.hero} title={pick(lang, game.title)} onClose={onClose}>
      <div className="text-center py-6" data-testid="game-result">
        <Trophy className="w-12 h-12 text-amber-300 mx-auto mb-3" />
        <p className="bs-title font-extrabold text-2xl mb-1">
          +{result.points} {lang === "es" ? "puntos" : "points"}
        </p>
        <p className="bs-muted mb-3">
          {lang === "es" ? "Puntaje" : "Score"}: {result.score}/{result.total}
        </p>
        {line && (
          <p className="bs-amber italic font-semibold mb-5 max-w-md mx-auto">"{line}"</p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {hasNextLevel && (
            <button onClick={onNext} className="bs-cta justify-center" data-testid="game-next-level">
              {lang === "es" ? `Nivel ${level + 1}` : `Level ${level + 1}`} <ChevronRight className="w-4 h-4" />
            </button>
          )}
          <button onClick={onReplay} className="bs-chip justify-center px-6 py-3" data-testid="game-replay">
            {lang === "es" ? "Jugar otra vez" : "Play Again"}
          </button>
          <button onClick={onClose} className="bs-chip justify-center px-6 py-3">
            {lang === "es" ? "Volver" : "Back to Games"}
          </button>
        </div>
      </div>
    </GameShell>
  );
};

// ---------- Level select ----------
const LevelSelect = ({ game, lang, bestLevel, onPick, onBack }) => (
  <GameShell hero={game.hero} title={pick(lang, game.title)} onClose={onBack}>
    <p className="bs-muted text-sm mb-4">{pick(lang, game.desc)}</p>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`level-select-${game.id}`}>
      {game.levels.map((lvl, idx) => {
        const num = idx + 1;
        const unlocked = num <= bestLevel + 1; // can play any level up to best+1
        return (
          <button
            key={idx}
            disabled={!unlocked}
            onClick={() => unlocked && onPick(num)}
            data-testid={`level-${game.id}-${num}`}
            className={`p-4 rounded-2xl border text-left transition-colors ${
              unlocked
                ? "bg-white/5 border-white/10 hover:bg-white/15 text-white"
                : "bg-white/[0.03] border-white/5 text-blue-100/40 cursor-not-allowed"
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="font-extrabold text-lg">{pick(lang, lvl.name)}</p>
              {!unlocked && <Lock className="w-4 h-4" />}
            </div>
            <p className="bs-amber text-xs font-bold">+{lvl.points} {lang === "es" ? "máx." : "max"}</p>
          </button>
        );
      })}
    </div>
    <div className="mt-4 flex justify-between items-center">
      <button onClick={onBack} className="bs-chip" data-testid="level-back">
        <ChevronLeft className="w-4 h-4" /> {lang === "es" ? "Volver" : "Back"}
      </button>
      <span className="bs-muted text-xs">
        {lang === "es" ? "Tu mejor nivel" : "Your best level"}: {bestLevel}
      </span>
    </div>
  </GameShell>
);

// ---------- Runner orchestrator ----------
const GameRunner = ({ game, startLevel, onClose, onCompleted }) => {
  const lang = useLang();
  const [level, setLevel] = useState(startLevel);
  const [phase, setPhase] = useState("playing"); // 'playing' | 'result'
  const [result, setResult] = useState(null);
  const [replayKey, setReplayKey] = useState(0);

  const finish = async ({ score, points, total }) => {
    const r = { score, points, total: total ?? game.levels[level - 1]?.questions?.length ?? 0 };
    setResult(r);
    setPhase("result");
    try {
      await axios.post("/games/play", {
        game_id: game.id,
        hero: game.hero,
        points_earned: points,
        score,
        level,
        protective_factor: game.protective_factor,
      });
    } catch {
      /* swallow */
    }
    onCompleted && onCompleted();
  };

  if (phase === "result") {
    return (
      <ResultScreen
        game={game}
        level={level}
        result={result}
        lang={lang}
        hasNextLevel={level < game.levels.length}
        onReplay={() => { setReplayKey((k) => k + 1); setPhase("playing"); }}
        onNext={() => { setLevel(level + 1); setReplayKey((k) => k + 1); setPhase("playing"); }}
        onClose={onClose}
      />
    );
  }

  const Runner = RUNNERS[game.type];
  return (
    <GameShell hero={game.hero} title={`${pick(lang, game.title)} · ${lang === "es" ? "Nivel" : "Level"} ${level}`} onClose={onClose}>
      <Runner key={`${game.id}-${level}-${replayKey}`} game={game} level={level} lang={lang} onFinish={finish} />
    </GameShell>
  );
};

// ---------- Top-level ----------
const Games = () => {
  const lang = useLang();
  // current selection state machine: catalogue → level select → runner
  const [selectedGame, setSelectedGame] = useState(null);
  const [runLevel, setRunLevel] = useState(null);
  const [stats, setStats] = useState({ total_points: 0, by_game: {} });

  const refresh = () =>
    axios.get("/games/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  if (selectedGame && runLevel) {
    return (
      <div data-testid="games-runner">
        <GameRunner
          game={selectedGame}
          startLevel={runLevel}
          onClose={() => { setRunLevel(null); setSelectedGame(null); refresh(); }}
          onCompleted={() => refresh()}
        />
      </div>
    );
  }

  if (selectedGame) {
    const bestLevel = stats.by_game[selectedGame.id]?.best_level || 1;
    return (
      <LevelSelect
        game={selectedGame}
        lang={lang}
        bestLevel={bestLevel}
        onPick={(num) => setRunLevel(num)}
        onBack={() => setSelectedGame(null)}
      />
    );
  }

  return (
    <div data-testid="games-catalogue">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="bs-title text-2xl font-extrabold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-300" />{" "}
            {lang === "es" ? "Juegos de Héroe" : "Hero Games"}
          </h2>
          <p className="bs-muted text-sm">
            {lang === "es"
              ? "4 juegos por héroe · varios niveles · puntos por factores protectores."
              : "4 games per hero · multiple levels · points for protective factors."}
          </p>
        </div>
        <div className="bs-glass-soft px-4 py-2 flex items-center gap-2" data-testid="games-points-total">
          <Award className="w-5 h-5 text-amber-300" />
          <div>
            <p className="text-[10px] bs-muted uppercase tracking-widest">
              {lang === "es" ? "Tus puntos" : "Your Points"}
            </p>
            <p className="bs-title font-extrabold text-xl leading-none">{stats.total_points}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {HERO_ORDER.map((hk) => {
          const tr = HEROES[hk];
          const games = GAMES[hk];
          return (
            <div key={hk} data-testid={`games-section-${hk}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`hero-avatar w-10 h-12 ${tr.imageBg}`}>
                  <img src={tr.image} alt="" />
                </div>
                <h3 className="bs-title text-lg font-extrabold capitalize">{tr.key}</h3>
                <Badge className="bg-white/10 text-amber-300 border-amber-300/30">
                  {games.length} {lang === "es" ? "juegos" : "games"}
                </Badge>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {games.map((g) => {
                  const my = stats.by_game[g.id];
                  const pfLabel = PROTECTIVE_FACTORS[g.protective_factor];
                  return (
                    <Card
                      key={g.id}
                      className="bs-glass border-0 p-4 flex flex-col"
                      data-testid={`game-card-${g.id}`}
                    >
                      <p className="bs-amber text-[10px] font-bold uppercase tracking-widest mb-1">
                        {pfLabel && pick(lang, pfLabel)}
                      </p>
                      <h4 className="bs-title font-extrabold text-base leading-tight mb-1">
                        {pick(lang, g.title)}
                      </h4>
                      <p className="bs-muted text-xs mb-3 line-clamp-3">{pick(lang, g.desc)}</p>
                      <div className="flex items-center gap-2 text-xs bs-muted mb-3 flex-wrap">
                        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                        {g.levels.length} {lang === "es" ? "niveles" : "levels"}
                        {my && (
                          <>
                            <span className="mx-1">·</span>
                            <Award className="w-3.5 h-3.5 text-amber-300" />
                            {lang === "es" ? "Nivel" : "Lvl"} {my.best_level}
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => setSelectedGame(g)}
                        className="bs-cta justify-center mt-auto"
                        data-testid={`play-${g.id}`}
                      >
                        {lang === "es" ? "Jugar" : "Play"} <ChevronRight className="w-4 h-4" />
                      </button>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Games;
